import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { upcomingEvent, NOTIFY_ENDPOINT } from "../../data/collections";
import { API_URL } from "../../config";
import { useSiteAssets, optimized } from "../../siteAssets";
import styles from "./styles.module.css";

const SEEN_KEY = "lormami_event_seen";

const EventPopup = () => {
  const assets = useSiteAssets();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  // Show once per browser session, shortly after entry
  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem(SEEN_KEY);
    } catch (e) {
      /* storage unavailable */
    }
    if (seen) return;
    const timer = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      // Register in the store database (sends the welcome email server-side)
      const res = await fetch(`${API_URL}/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "popup" }),
      });
      // Backup ping to the store inbox
      fetch(NOTIFY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email,
          _subject: `Community registration: ${upcomingEvent.title}`,
        }),
      }).catch(() => {});
      setStatus(res.ok ? "done" : "error");
    } catch (err) {
      setStatus("error");
    }
  };

  if (!open || !upcomingEvent) return null;

  const eventDate =
    upcomingEvent.date &&
    new Date(upcomingEvent.date).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className={styles.scrim} onClick={close}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={upcomingEvent.title}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.close}
          onClick={close}
          aria-label="Close"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        <div
          className={styles.media}
          style={{
            backgroundImage: `url(${
              assets.event ? optimized(assets.event) : upcomingEvent.image
            })`,
          }}
        />

        <div className={styles.body}>
          <span className={styles.eyebrow}>
            <span className={styles.dot} />
            {upcomingEvent.date
              ? `Upcoming Event · ${upcomingEvent.location}`
              : "The LORMAMI Community"}
          </span>
          <h3 className={styles.title}>{upcomingEvent.title}</h3>
          {eventDate && <p className={styles.date}>{eventDate}</p>}
          <p className={styles.blurb}>{upcomingEvent.blurb}</p>

          {status === "done" ? (
            <p className={styles.done}>
              You’re registered — we’ll email you the details shortly.
            </p>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit}>
              <input
                type="email"
                required
                className={styles.input}
                placeholder="Enter your email to register"
                aria-label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                type="submit"
                className={styles.submit}
                disabled={status === "sending"}
              >
                {status === "sending" ? "Registering…" : "Register"}
              </button>
              {status === "error" && (
                <span className={styles.err}>Something went wrong — try again.</span>
              )}
            </form>
          )}

          <Link to="/collections" className={styles.link} onClick={close}>
            Explore the collections →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EventPopup;
